import React from 'react';
import { useSelector } from 'react-redux';
import { Slide } from '@mui/material';
import { ButtonV2 } from '@bbkAdminComponents/buttons';
import { selectCompanyInternalName } from '@bbkAdminRedux/app/selectors';
import { PLANS } from '@bbkAdminRedux/rtkq/checkout.plans';
import { checkoutSlice } from '@bbkAdminRedux/rtkq/checkout.slice';
import { useSelectCurrentApp } from '@bbkAdminRedux/rtkq/current-user.slice';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { useUrl } from '@bbkAdminUtils/nav';
import { useGetProductLicense, usePlans } from '@bbkAdminUtils/plans';
import { Link, useLocation, useRouteMatch } from '@bbkShared/react-router';

export const GetStartedBanner: React.FC = () => {
  const createUrl = useUrl();
  const location = useLocation();
  const currentApp = useSelectCurrentApp();
  const companyKey = useSelector(selectCompanyInternalName);
  const productLicense = useGetProductLicense();
  const growthV2License = productLicense?.growthV2License;
  const isGetStartedEligible = currentApp?.get_started_status === 'ELIGIBLE';
  const inAdminApp = useRouteMatch('/company/:companyKey/app/:appKey');

  const showGetStartedGrowthV2 =
    Boolean(inAdminApp) &&
    Boolean(growthV2License) &&
    Boolean(currentApp?.show_play_demo_data);
  const showBanner =
    Boolean(inAdminApp) && isGetStartedEligible && !showGetStartedGrowthV2;

  const pathname = location.pathname;
  const isOnGetStartedGrowthV2Page =
    pathname.endsWith(AuthorizedPaths.getStartedGrowthV2) ||
    pathname.endsWith(`${AuthorizedPaths.getStartedGrowthV2}/`);

  const { isStarterFreePlan, isStarterPremiumPlan } = usePlans();
  const isStarterPlan = isStarterFreePlan || isStarterPremiumPlan;
  const BANNER_MESSAGE_STARTER =
    'Create and launch your first cancel page to start seeing data in your dashboard';
  const BANNER_MESSAGE_TRANSITION =
    'Add loss aversion cards and offers to your existing cancel page to retain more customers.';
  const BANNER_MESSAGE_DEFLECTION =
    'Create and launch your cancel page with offers to retain more customers.';
  const BANNER_MESSAGE_GROWTH_V2 =
    'Welcome to Chargebee Growth! Create your first Play to start unlocking new revenue opportunities.';

  const { data: subsHistory } =
    checkoutSlice.endpoints.getSubscriptionsHistory.useQuery(
      { companyKey },
      { skip: companyKey === '_' }
    );
  const wasOnStarterPreviously = subsHistory?.find((x) =>
    [PLANS.STARTER_PREMIUM, PLANS.STARTER_FREE].includes(x.subscription_plan)
  );

  const getBannerMessage = () => {
    if (isStarterPlan) {
      return BANNER_MESSAGE_STARTER;
    } else if (wasOnStarterPreviously) {
      return BANNER_MESSAGE_TRANSITION;
    } else {
      return BANNER_MESSAGE_DEFLECTION;
    }
  };

  return (
    <>
      <Slide
        direction="down"
        in={showGetStartedGrowthV2 && !isOnGetStartedGrowthV2Page}
        mountOnEnter
        unmountOnExit
      >
        <div className="tw-flex tw-justify-between tw-bg-cbrprimary-800 tw-px-4 tw-text-white">
          <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-py-3">
            <div>
              <div>{BANNER_MESSAGE_GROWTH_V2}</div>
            </div>
            <Link to={createUrl(AuthorizedPaths.getStartedGrowthV2)}>
              <ButtonV2 secondary className="tw-ml-4">
                Get started
              </ButtonV2>
            </Link>
          </div>
        </div>
      </Slide>
      <Slide direction="down" in={showBanner} mountOnEnter unmountOnExit>
        <div className="tw-flex tw-justify-between tw-bg-cbrprimary-800 tw-px-4 tw-text-white">
          <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-py-3">
            <div>
              <div>{getBannerMessage()}</div>
            </div>
            <Link to={createUrl(AuthorizedPaths.getStarted)}>
              <ButtonV2 secondary className="tw-ml-4">
                Get started
              </ButtonV2>
            </Link>
          </div>
        </div>
      </Slide>
    </>
  );
};
